import { lazy, Suspense, useEffect, useState } from 'react'
import { ProjectGallery } from './gallery/ProjectGallery'
import { applyDocumentLocale, localeFromEnvironment, stringsFor } from './i18n'
import { ProjectViewer } from './viewer/ProjectViewer'

const BaseEditor = lazy(() => import('./editor/BaseEditor'))

export function App() {
  const [route, setRoute] = useState(() => routeFromLocation())
  const [importedFile, setImportedFile] = useState<File | undefined>()
  const locale = localeFromEnvironment()
  const strings = stringsFor(locale)

  useEffect(() => {
    applyDocumentLocale(locale)
  }, [locale])

  useEffect(() => {
    const handleHashChange = () => setRoute(routeFromLocation())
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  function openMode(nextMode: AppMode) {
    window.location.hash = nextMode === 'gallery' ? '' : nextMode
    setRoute(routeFromLocation())
  }

  if (route.kind === 'legal') {
    return <LegalPage page={route.page} />
  }

  if (route.mode === 'base') {
    return (
      <Suspense fallback={<main className="viewer-shell"><div className="viewer-empty">{strings.common.baseEditorLoading}</div></main>}>
        <BaseEditor onOpenFree={() => openMode('gallery')} />
      </Suspense>
    )
  }

  if (route.mode === 'free') {
    return <ProjectViewer onOpenBase={() => openMode('base')} onOpenGallery={() => openMode('gallery')} initialFile={importedFile} />
  }

  return (
    <ProjectGallery
      locale={locale}
      onOpenFree={() => openMode('free')}
      onOpenBase={() => openMode('base')}
      onImportProject={(file) => { setImportedFile(file); openMode('free') }}
    />
  )
}

type AppMode = 'gallery' | 'free' | 'base'
type LegalPageID = 'privacy' | 'terms' | 'support'
type AppRoute = { kind: 'app'; mode: AppMode } | { kind: 'legal'; page: LegalPageID }

function routeFromLocation(): AppRoute {
  const page = window.location.pathname.replace(/^\/+|\/+$/g, '')
  if (page === 'privacy' || page === 'terms' || page === 'support') {
    return { kind: 'legal', page }
  }
  const hash = window.location.hash.replace(/^#/, '')
  const mode: AppMode = hash === 'base' ? 'base' : hash === 'free' ? 'free' : 'gallery'
  return { kind: 'app', mode }
}

function LegalPage({ page }: { page: LegalPageID }) {
  const content = legalContent[page]
  return (
    <main className="legal-shell">
      <nav className="legal-nav" aria-label="Pagine legali">
        <a href="/">ForceCAD</a>
        <span aria-hidden="true">/</span>
        <a href="/privacy">Privacy</a>
        <a href="/terms">Termini</a>
        <a href="/support">Assistenza</a>
      </nav>
      <article className="legal-document">
        <p className="eyebrow">{content.eyebrow}</p>
        <h1>{content.title}</h1>
        <p className="legal-updated">Ultimo aggiornamento: 31 luglio 2026</p>
        <LegalLanguageBlock sections={content.it} />
        <div className="legal-language-divider" aria-hidden="true" />
        <p className="eyebrow">English</p>
        <h1>{content.englishTitle}</h1>
        <p className="legal-updated">Last updated: July 31, 2026</p>
        <LegalLanguageBlock sections={content.en} />
      </article>
    </main>
  )
}

function LegalLanguageBlock({ sections }: { sections: LegalSection[] }) {
  return (
    <>
      {sections.map((section) => (
        <section key={section.title}>
          <h2>{section.title}</h2>
          {section.body.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </section>
      ))}
    </>
  )
}

type LegalSection = { title: string; body: string[] }

const legalContent: Record<LegalPageID, {
  eyebrow: string
  title: string
  englishTitle: string
  it: LegalSection[]
  en: LegalSection[]
}> = {
  privacy: {
    eyebrow: 'Privacy',
    title: 'Informativa privacy ForceCAD',
    englishTitle: 'ForceCAD Privacy Policy',
    it: [
      {
        title: 'Dati raccolti dall’app',
        body: [
          'ForceCAD non richiede un account ForceCAD, non usa sistemi di tracciamento pubblicitario e non raccoglie progetti, geometrie, immagini, carichi o risultati sui server dello sviluppatore.',
          'I progetti restano sul dispositivo, nel browser o nella cartella esterna scelta dall’utente. ForceCAD non invia questi dati per verificare gli acquisti.',
        ],
      },
      {
        title: 'Acquisti e abbonamenti',
        body: [
          'Su iOS gli acquisti e gli abbonamenti sono elaborati dall’App Store secondo le condizioni e l’informativa privacy di Apple.',
          'Sul web le licenze saranno elaborate dal merchant indicato nella pagina di acquisto. Le licenze web e gli acquisti iOS sono separati e non trasferibili.',
        ],
      },
      {
        title: 'Assistenza',
        body: [
          'Se contatti l’assistenza, l’indirizzo email e le informazioni che invii vengono usati soltanto per rispondere alla richiesta.',
          'Contatto: contact@log4bike.com.',
        ],
      },
    ],
    en: [
      {
        title: 'Data collected by the app',
        body: [
          'ForceCAD does not require a ForceCAD account, does not use advertising tracking, and does not collect projects, geometries, images, loads, or results on the developer’s servers.',
          'Projects remain on the device, in the browser, or in the external folder selected by the user. ForceCAD does not send this data to verify purchases.',
        ],
      },
      {
        title: 'Purchases and subscriptions',
        body: [
          'On iOS, purchases and subscriptions are processed by the App Store according to Apple’s terms and privacy policy.',
          'On the web, licenses will be processed by the merchant shown on the purchase page. Web licenses and iOS purchases are separate and are not transferable.',
        ],
      },
      {
        title: 'Support',
        body: [
          'If you contact support, your email address and the information you send are used only to reply to your request.',
          'Contact: contact@log4bike.com.',
        ],
      },
    ],
  },
  terms: {
    eyebrow: 'Termini',
    title: 'Termini d’uso ForceCAD',
    englishTitle: 'ForceCAD Terms of Use',
    it: [
      {
        title: 'Uso dell’app',
        body: [
          'ForceCAD è uno strumento didattico e divulgativo per esplorare statica e cinematica dei telai piani. I risultati dipendono dalle semplificazioni del modello e dai dati inseriti dall’utente.',
          'Non usare ForceCAD, così com’è, per progettare, verificare o mettere in esercizio strutture reali. Per applicazioni professionali servono un tecnico abilitato, software validato secondo le normative vigenti e una verifica indipendente dei risultati.',
        ],
      },
      {
        title: 'Licenze e servizio web',
        body: [
          'Gli acquisti permanenti sbloccano le funzioni indicate finché il servizio o l’app compatibile restano disponibili. Se in futuro il servizio web venisse dismesso, sarà dato un preavviso ragionevole.',
          'I file dei progetti restano dell’utente. Import ed export manuale dei file di progetto non dipendono da una licenza.',
        ],
      },
      {
        title: 'Responsabilità',
        body: [
          'L’autore non si assume responsabilità per decisioni tecniche o danni derivanti dall’uso dell’app.',
          'Le condizioni possono essere aggiornate quando cambiano prodotto, piattaforme o requisiti normativi.',
        ],
      },
    ],
    en: [
      {
        title: 'Use of the app',
        body: [
          'ForceCAD is an educational tool for exploring the statics and kinematics of planar frames. Results depend on model simplifications and on the data entered by the user.',
          'Do not use ForceCAD, as provided, to design, verify, or put real structures into service. Professional applications require a qualified engineer, software validated under applicable regulations, and independent verification of the results.',
        ],
      },
      {
        title: 'Licenses and web service',
        body: [
          'Permanent purchases unlock the stated features while the compatible service or app remains available. If the web service is discontinued in the future, reasonable prior notice will be provided.',
          'Project files remain the user’s property. Manual import and export of project files do not depend on a license.',
        ],
      },
      {
        title: 'Liability',
        body: [
          'The author accepts no responsibility for technical decisions or damages arising from use of the app.',
          'These terms may be updated when the product, platforms, or legal requirements change.',
        ],
      },
    ],
  },
  support: {
    eyebrow: 'Assistenza',
    title: 'Assistenza ForceCAD',
    englishTitle: 'ForceCAD Support',
    it: [
      {
        title: 'Contatto',
        body: [
          'Per richieste di supporto scrivi a contact@log4bike.com.',
          'Includi versione dell’app, dispositivo o browser, passaggi per riprodurre il problema e, se utile, uno screenshot. Non inviare dati sensibili non necessari.',
        ],
      },
      {
        title: 'Acquisti',
        body: [
          'Su iOS gli acquisti e gli abbonamenti sono gestiti dall’App Store. La funzione Ripristina acquisti usa l’account Apple con cui hai ottenuto il prodotto.',
          'Base e Studio sono licenze permanenti. Sync è un abbonamento opzionale per il workspace esterno fra dispositivi.',
        ],
      },
    ],
    en: [
      {
        title: 'Contact',
        body: [
          'For support requests, write to contact@log4bike.com.',
          'Include the app version, device or browser, steps to reproduce the issue, and a screenshot if useful. Do not send unnecessary sensitive data.',
        ],
      },
      {
        title: 'Purchases',
        body: [
          'On iOS, purchases and subscriptions are managed by the App Store. Restore Purchases uses the Apple account used to obtain the product.',
          'Base and Studio are permanent licenses. Sync is an optional subscription for the external workspace across devices.',
        ],
      },
    ],
  },
}
